// src/ecrans/portes.js — LA SEULE FAÇON DONT LES ÉCRANS NEST LISENT (P-4.24).
//
// Les écrans Accueil, Calendrier, Opérations et Voyageurs ne lisent JAMAIS
// une table depuis le navigateur ni l'ancien stockage `fbz8-*` : ils
// demandent au serveur par une porte (`POST /api/beds24-mapping { action }`,
// portes déclarées dans lib/routes/ecrans.js et lib/routes/*.js).
//
// UNE LECTURE RATÉE SE DIT. Chaque lecture rend l'une de trois formes, jamais
// une liste vide à la place d'une panne :
//   · { etat: "lecture" }                    — on lit (« On lit… ») ;
//   · { etat: "ok", data }                   — la réponse du serveur ;
//   · { etat: "echec", motif }               — l'écran affiche l'erreur.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../supabase";

const ENDPOINT = "/api/beds24-mapping";

export const LECTURE = Object.freeze({ etat: "lecture" });

/** Le message affiché pour une lecture ratée : le statut, jamais une donnée. */
export function motifEchec(statut, json) {
  if (statut === 401) return "Session expirée : reconnectez-vous.";
  if (statut === 403) return "Lecture refusée pour ce compte.";
  if (json && json.code === "lecture_impossible")
    return "Le serveur n'a pas pu lire ces données.";
  if (json && typeof json.error === "string" && json.error)
    return `Lecture impossible : ${json.error}`;
  return statut ? `Lecture impossible (erreur ${statut}).` : "Lecture impossible.";
}

/**
 * Appelle une porte. Ne lève jamais : rend { etat: "ok", data } ou
 * { etat: "echec", motif }. Une réponse 200 sans `ok: true` est un échec
 * (une porte qui répond à côté ne passe pas pour « rien à montrer »).
 */
export async function lirePorte(action, params = {}, deps = {}) {
  const f = deps.fetch || (typeof fetch === "function" ? fetch : null);
  let jeton = null;
  try {
    const lire = deps.session || (() => supabase.auth.getSession());
    const r = await lire();
    jeton = r && r.data && r.data.session ? r.data.session.access_token : null;
  } catch (e) {
    return { etat: "echec", motif: "Session illisible : reconnectez-vous." };
  }
  if (!jeton) return { etat: "echec", motif: "Session expirée : reconnectez-vous." };
  let res;
  try {
    res = await f(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jeton}`,
      },
      body: JSON.stringify({ ...params, action }),
    });
  } catch (e) {
    return { etat: "echec", motif: "Connexion impossible. Réessayez." };
  }
  let json = null;
  try {
    json = await res.json();
  } catch (e) {
    json = null;
  }
  if (!res.ok) return { etat: "echec", motif: motifEchec(res.status, json) };
  // Les portes des écrans répondent `ok: true` ; les portes plus anciennes
  // (opérateur, horaires, prospects) rendent un objet sans ce drapeau :
  // `accepteSansOk` les admet, mais `ok: false` reste toujours un échec.
  const forme = json && typeof json === "object" && !Array.isArray(json);
  if (!forme || json.ok === false || (json.ok !== true && !deps.accepteSansOk))
    return {
      etat: "echec",
      motif:
        forme && typeof json.motif === "string" && json.motif
          ? json.motif
          : "Réponse du serveur illisible.",
    };
  return { etat: "ok", data: json };
}

/**
 * Lit une porte après le premier affichage, relit quand `cle` change ou sur
 * `relire()`. Un écran démonté n'écrit plus son état.
 */
export function useLecture(action, params, cle = "", deps) {
  const [lecture, setLecture] = useState(LECTURE);
  const vivant = useRef(true);
  const paramsJson = JSON.stringify(params || {});
  const [tour, setTour] = useState(0);
  useEffect(() => {
    vivant.current = true;
    return () => {
      vivant.current = false;
    };
  }, []);
  useEffect(() => {
    if (!action) return undefined;
    let actif = true;
    setLecture(LECTURE);
    const id = setTimeout(() => {
      lirePorte(action, JSON.parse(paramsJson), deps).then((r) => {
        if (actif && vivant.current) setLecture(r);
      });
    }, 0);
    return () => {
      actif = false;
      clearTimeout(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, paramsJson, cle, tour]);
  const relire = useCallback(() => setTour((t) => t + 1), []);
  return [lecture, relire];
}

/** Le nombre affiché d'une lecture : « … » en lecture, « — » en échec. */
export function nombreDe(lecture, calcul) {
  if (!lecture || lecture.etat === "lecture") return "…";
  if (lecture.etat !== "ok") return "—";
  const n = calcul(lecture.data);
  return Number.isFinite(n) ? n : "—";
}

/**
 * Les portes du site (`GET /api/sites?action=…`) : demandes directes, avis.
 * Même contrat que `lirePorte` : { etat: "ok", data } ou { etat: "echec" }.
 */
export async function lireSites(action, params = {}, deps = {}) {
  const f = deps.fetch || (typeof fetch === "function" ? fetch : null);
  let jeton = null;
  try {
    const lire = deps.session || (() => supabase.auth.getSession());
    const r = await lire();
    jeton = r && r.data && r.data.session ? r.data.session.access_token : null;
  } catch (e) {
    return { etat: "echec", motif: "Session illisible : reconnectez-vous." };
  }
  if (!jeton) return { etat: "echec", motif: "Session expirée : reconnectez-vous." };
  const q = new URLSearchParams({ ...params, action });
  let res;
  try {
    res = await f(`/api/sites?${q.toString()}`, { headers: { Authorization: `Bearer ${jeton}` } });
  } catch (e) {
    return { etat: "echec", motif: "Connexion impossible. Réessayez." };
  }
  let json = null;
  try {
    json = await res.json();
  } catch (e) {
    json = null;
  }
  if (!res.ok) return { etat: "echec", motif: motifEchec(res.status, json) };
  if (!json || json.ok !== true) return { etat: "echec", motif: "Réponse du serveur illisible." };
  return { etat: "ok", data: json };
}

/** `useLecture` pour les portes du site. */
export function useLectureSites(action, params) {
  const [lecture, setLecture] = useState(LECTURE);
  const paramsJson = JSON.stringify(params || {});
  useEffect(() => {
    let actif = true;
    setLecture(LECTURE);
    const id = setTimeout(() => {
      lireSites(action, JSON.parse(paramsJson)).then((r) => actif && setLecture(r));
    }, 0);
    return () => {
      actif = false;
      clearTimeout(id);
    };
  }, [action, paramsJson]);
  return lecture;
}
